import { Injectable } from '@angular/core';
import { ActiveState, EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { AssetAllocation, KpiCurrent, PrivateAsset } from './private-asset.model';

export interface PrivateAssetState
    extends EntityState<PrivateAsset, number>,
        ActiveState {
    totalKpiCurrent: KpiCurrent;
    assetAllocations: AssetAllocation[];
    activeAssetType: string;
    ui: {
        [key: string]: {
            isLoading: boolean;
        };
    };
}

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'private-assets', cache: { ttl: 3600000 }, idKey: 'instrumentId' })
export class PrivateAssetStore extends EntityStore<PrivateAssetState> {
    constructor() {
        super();
    }

    /**
     * When instruments are added, make sure valuations are populated with ids based on date.
     */
    akitaPreAddEntity(privateAsset: PrivateAsset) {
        if (privateAsset?.marketValues?.length > 0) {
            return this.getPrivateAssetWithSortedMarketValues(privateAsset);
        }
        return privateAsset;
    }

    /**
     * When instrument is updated, make sure valuations are populated with ids based on date.
     */
    akitaPreUpdateEntity(prevPrivateAsset: PrivateAsset, nextPrivateAsset: PrivateAsset) {
        if (nextPrivateAsset?.marketValues?.length > 0) {
            return this.getPrivateAssetWithSortedMarketValues(nextPrivateAsset);
        }
        return nextPrivateAsset;
    }

    getPrivateAssetWithSortedMarketValues(privateAsset: PrivateAsset) {
        const { marketValues, portfolioCcy } =
            this.marketValuesSortedInDescendingOrder(privateAsset);

        return {
            ...privateAsset,
            ...portfolioCcy,
            ...marketValues
        };
    }

    marketValuesSortedInDescendingOrder(privateAsset: PrivateAsset) {
        // Sorted descending by date (most recent first)
        const sortDescending = (a, b) =>
            new Date(b.date).getTime() - new Date(a.date).getTime();

        return {
            marketValues: {
                marketValues: privateAsset.marketValues.sort(sortDescending)
            },
            portfolioCcy: {
                marketValues: privateAsset.portfolioCcy?.marketValues.sort(sortDescending)
            }
        };
    }
}
